// The boat's own photo for its home header (TOM-683, cloud ADR 0397): add,
// replace or remove it. Any owner may; every owner sees it. A boat with none
// keeps the marina scene.
//
// Every photo is drawn again as a JPEG before it uploads (prepareChatPhoto
// with redraw): that drops its location and other camera details, whatever
// its type, and shrinks a large one.

import { useRef, useState } from 'react';

import {
  boatPhotoErrorMessage,
  boatPhotoMayBe,
  boatPhotoUrl,
  BOAT_PHOTO_ACCEPT,
} from '../../lib/boatPhoto';
import { prepareChatPhoto } from '../../lib/chatAttachments';
import type { BoatPhoto } from '../../lib/useBoats';

interface BoatPhotoPanelProps {
  boatId: string;
  boatName: string;
  photo: BoatPhoto | null | undefined;
  /** Read the boat list again, so home and this panel show the change. */
  onChanged?: () => void;
}

export function BoatPhotoPanel({
  boatId,
  boatName,
  photo,
  onChanged,
}: BoatPhotoPanelProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const url = boatPhotoUrl(boatId, photo);

  async function send(method: 'PUT' | 'DELETE', body?: FormData): Promise<void> {
    const res = await fetch(`/v1/boats/${encodeURIComponent(boatId)}/photo`, {
      method,
      body,
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const json: unknown = await res.json().catch(() => null);
      throw new Error(boatPhotoErrorMessage(res.status, json));
    }
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file === undefined) return;
    setError(null);
    setSaved(null);
    if (!boatPhotoMayBe(file)) {
      setError('Choose a JPEG, PNG or WebP photo.');
      return;
    }
    setBusy('upload');
    try {
      const prepared = await prepareChatPhoto(file, { redraw: true });
      const form = new FormData();
      form.append('file', prepared, prepared.name);
      await send('PUT', form);
      setSaved(`Home shows the new photo of ${boatName}.`);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The photo didn’t save. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function onRemove(): Promise<void> {
    setError(null);
    setSaved(null);
    setBusy('remove');
    try {
      await send('DELETE');
      setSaved('Home shows the marina scene again.');
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The photo wasn’t removed. Try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="panel" data-testid="boat-settings-photo">
      <h3 id="boat-settings-photo-heading">Home photo</h3>
      <p className="muted">
        A photo of {boatName} for the top of its home, in place of the marina scene. Every owner
        sees it. Night shows no photo. A wide photo with the boat in the middle works best.
      </p>
      {url !== null ? (
        <img
          className="boat-photo-panel__preview"
          src={url}
          alt={`The home photo of ${boatName}`}
          width={photo?.width}
          height={photo?.height}
          data-testid="boat-photo-preview"
        />
      ) : null}
      <div className="boat-photo-panel__actions">
        <input
          ref={inputRef}
          className="sr-only"
          type="file"
          accept={BOAT_PHOTO_ACCEPT}
          tabIndex={-1}
          aria-hidden="true"
          data-testid="boat-photo-input"
          onChange={(e) => void onPick(e)}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy !== null}
          data-testid="boat-photo-choose"
          onClick={() => inputRef.current?.click()}
        >
          {busy === 'upload' ? 'Saving…' : url === null ? 'Add a photo' : 'Replace photo'}
        </button>
        {url !== null ? (
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            data-testid="boat-photo-remove"
            onClick={() => void onRemove()}
          >
            {busy === 'remove' ? 'Removing…' : 'Remove photo'}
          </button>
        ) : null}
      </div>
      {error !== null ? (
        <p
          className="alarm-form-err boat-photo-panel__note"
          role="alert"
          data-testid="boat-photo-error"
        >
          {error}
        </p>
      ) : null}
      {saved !== null ? (
        <p
          className="profile-confirm boat-photo-panel__note"
          role="status"
          data-testid="boat-photo-saved"
        >
          {saved}
        </p>
      ) : null}
    </div>
  );
}
